import {
  Button,
  Cell,
  FileInput,
  Image,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from "@telegram-apps/telegram-ui";
import {Currencies, Currency, Image as ImageType, Wish, WishInfo, Wishlist} from "@/api/types.ts";
import {ChangeEvent, useEffect, useState} from "react";
import {addWish, deleteWish, updateWish, uploadImage} from "@/api/apiMethods.ts";
import {ImageCropDialog} from "@/components/ImageCropDialog.tsx";
import {TbCoins, TbPhoto, TbTextPlus, TbWorld} from "react-icons/tb";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useTranslation} from "react-i18next";
import {mainButton} from "@telegram-apps/sdk-react";
import {setOnClickMainButton} from "@/helpers/tgButtons.ts";
import {haptic} from "@/helpers/haptic.ts";

export const WishForm = (
  { wish, defaultWishlistIds, onSaved, onDeleted, wishlists }:
  {
    wish?: Wish,
    defaultWishlistIds: string[],
    onSaved: (wish: Wish) => void,
    onDeleted: (wishId: string) => void,
    wishlists?: Wishlist[] | undefined,
  }
) => {
  const [ wishlistIds, setWishlistIds ] = useState<string[]>(wish?.wishlistIds || defaultWishlistIds);
  const [ title, setTitle ] = useState<string>(wish?.title || "");
  const [ titleValidateError, setTitleValidateError ] = useState<boolean>(false);
  const [ url, setUrl ] = useState<string>(wish?.url || "");
  const [ urlValidateError, setUrlValidateError ] = useState<boolean>(false);
  const [ priceNumber, setPriceNumber ] = useState<number | null>(wish?.price?.number || null);
  const [ currencyCode, setCurrencyCode ] = useState<string>(wish?.price?.currency || "USD");
  const [ description, setDescription ] = useState<string>(wish?.description || "");

  const [ imageToCrop, setImageToCrop ] = useState<File | null>(null);
  const [ newImage, setNewImage ] = useState<ImageType | null>(wish?.image || null);
  const [ isImageLoaded, setIsImageLoaded ] = useState<boolean>(newImage === null);
  const [ isDeleteInProgress, setIsDeleteInProgress ] = useState<boolean>(false);
  const [ isSaveInProgress, setIsSaveInProgress ] = useState<boolean>(false);
  const { showSnackbar } = useNavigation();
  const { t } = useTranslation();

  const saveWish = async () => {
    if (!isImageLoaded) {
      return;
    }
    if (!title) {
      haptic.notification('error');
      setTitleValidateError(true);
      showSnackbar("❌", "system.emptyTitle");
      return;
    }
    if (url && !url.match(/^https:\/\/.*/)) {
      haptic.notification('error');
      setUrlValidateError(true);
      showSnackbar("❌", "system.invalidUrl");
      return;
    }
    setTitleValidateError(false);

    let price: { currency: string, number: number } | null = null;
    if (priceNumber) {
      price = { currency: currencyCode, number: priceNumber };
    }

    setIsSaveInProgress(true);

    const newWish: WishInfo = {
      image: newImage,
      title: title,
      price: price,
      description: description,
      url: url,
      wishlistIds: wishlistIds,
    }
    try {
      if (wish) {
        wish = {...wish, ...newWish};
        const updatedWish = await updateWish(wish);
        onSaved(updatedWish);
      } else {
        const createdWish = await addWish(newWish);
        onSaved(createdWish);
      }
      haptic.notification('success');
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }
    setIsSaveInProgress(false);
  }

  useEffect(() => {
    setOnClickMainButton(async () => {
      await saveWish();
    })
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: isSaveInProgress,
      isEnabled: !isSaveInProgress,
      text: wish ? t('action.save') : t('wish.action.add'),
    });
  }, [isSaveInProgress, saveWish]);

  const onSelectImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (!target.files) {
      return;
    }
    const file = target.files[0];
    if (!file) {
      return;
    }
    haptic.impact('light');
    return setImageToCrop(file);
  }

  const onDelete = async () => {
    if (!wish) {
      return;
    }
    const doDelete = async (wish: Wish) => {
      try {
        await deleteWish(wish);
        haptic.notification('success');
        showSnackbar("🗑", "system.wishDeleted");
        onDeleted(wish.id);
      } catch (e) {
        haptic.notification('error');
        showSnackbar("❌", "system.unknowError");
      }
    };
    setIsDeleteInProgress(true);
    haptic.notification('warning');
    const isConfirmed = confirm(t('wish.dialog.delete.text'));
    if (isConfirmed) {
      await doDelete(wish);
    }
    setIsDeleteInProgress(false);
  }

  const onCropped = async (fileBlob: Blob) => {
    setNewImage(null);
    setIsImageLoaded(false);
    const file = new File([fileBlob], "wish", { type: fileBlob.type });
    const newImage = await uploadImage(file);
    setNewImage(newImage);
    setImageToCrop(null);
    setIsImageLoaded(true);
    haptic.notification('success');
  }

  const toggleWishlist = (wishlistId: string) => {
    haptic.selection();
    const exists = wishlistIds.includes(wishlistId);
    if (!exists) {
      wishlistIds.push(wishlistId);
      setWishlistIds([...wishlistIds]);
    } else {
      const index = wishlistIds.indexOf(wishlistId);
      wishlistIds.splice(index, 1);
      setWishlistIds([...wishlistIds]);
    }
  }

  const onPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (/^\d.*$/.test(inputValue) || inputValue === "") {
      if (inputValue === "") {
        setPriceNumber(null);
        return;
      }
      setPriceNumber(+inputValue);
    }
  }

  let wishlistRows = null;
  if (wishlists && wish?.status !== "gifted") {
    wishlistRows = <Section className={"mb-3"} header={t('wish.action.addToWishlists')}>
      {wishlists.map((wishlist) => (
        <Cell Component={"label"}
              key={"preview_" + wishlist.id}
              onClick={() => toggleWishlist(wishlist.id)}
              after={<Switch onChange={() => toggleWishlist(wishlist.id)}
                checked={wishlistIds.includes(wishlist.id)}
              />}
              value={wishlist.id}>
          {wishlist.title}
        </Cell>
      ))}
    </Section>
  }

  return (<div className={"flex flex-col gap-3"}>
    {imageToCrop && <ImageCropDialog onCrop={onCropped} file={imageToCrop} onClose={() => setImageToCrop(null)}/>}
    <div className={"flex justify-center items-center"}>
      <div className={"relative overflow-hidden flex justify-center items-center p-3"}>
        {!isImageLoaded && <Spinner className={"absolute z-50"} size={"l"}/>}
        <Image size={96}
               id={"wish-image"}
               style={{backgroundColor: '#00000010'}}
               fallbackIcon={<TbPhoto size={50}/>}
               onLoad={() => setIsImageLoaded(true)}
               src={newImage?.sizes[newImage?.sizes.length - 1].link || ""}
               className={"rounded-lg"} />
      </div>
      <FileInput label={t('action.selectPhoto')}
                 onChange={onSelectImage}
                 accept="image/*,capture=camera" />
    </div>
    <Input placeholder={t('wish.placeholder.url')}
           type={"url"}
           pattern="https://.*"
           value={url}
           status={urlValidateError ? "error" : "default"}
           onFocus={() => setUrlValidateError(false)}
           before={<TbWorld size={20}/>}
           onChange={(e) => setUrl(e.target.value)} />
    <Input placeholder={t('wish.placeholder.title')}
           before={<TbTextPlus size={20}/>}
           onFocus={() => setTitleValidateError(false)}
           onBlur={() => setTitleValidateError(!title)}
           status={titleValidateError ? "error" : "default"}
           value={title}
           onChange={(e) => setTitle(e.target.value)} />

    <div className={"flex justify-around gap-3"}>
      <Input className={"w-full"}
             id={"priceField"}
             before={<TbCoins size={20}/>}
             type={"text"}
             pattern="\d*"
             placeholder={t('wish.placeholder.price')}
             value={priceNumber? priceNumber : ''}
             onChange={onPriceChange} />
      <Select
        onChange={(e) => {
          haptic.selection();
          setCurrencyCode(e.target.value);
        }}
        defaultValue={currencyCode}>
        {Currencies.map((currency:Currency) => (
          <option key={"currency"+currency.code} value={currency.code}>{currency.symbol}</option>
        ))}
      </Select>
    </div>

    <Textarea placeholder={t('wish.placeholder.description')} value={description} onChange={(e) => setDescription(e.target.value)} />

    {wishlistRows}

    {import.meta.env.MODE === 'development' && <Button disabled={isSaveInProgress}
            loading={isSaveInProgress}
            onClick={saveWish}
            stretched={true}>
      {wish ? t('action.save') : t('wish.action.add')}
    </Button>}
    {wish && <Button stretched={true}
            disabled={isDeleteInProgress}
            loading={isDeleteInProgress}
            onClick={onDelete}
            mode={"plain"} size={"s"}>
      <div className={"text-color-destroy"}>{t('wish.action.delete')}</div>
    </Button>}
  </div>);
}
